import type { OpenedDocument } from './documents.ts';
import { loadText } from './documents.ts';

/**
 * Clientseitiger Volltextindex (Konzept 3.10).
 *
 * Der Index existiert ausschließlich im Arbeitsspeicher dieser Seite. Es gibt
 * bewusst keine Persistenz in IndexedDB: sie würde den Index eine Sitzung
 * überdauern lassen, und genau das soll nicht passieren – weder nach dem
 * Abmelden des Kunden noch nach Ablauf einer Buddy-Freigabe.
 *
 * Eine eigene kleine Implementierung statt einer Suchbibliothek, weil das
 * Verfahren überschaubar bleiben soll: was hier passiert, ist nachvollziehbar,
 * ohne eine Fremdbibliothek auf ihren Umgang mit Klartext prüfen zu müssen.
 */

interface IndexEntry {
  documentId: string;
  filename: string;
  text: string;
  /** Wortstämme → Häufigkeit, für die Bewertung der Treffer. */
  terms: Map<string, number>;
}

export interface SearchHit {
  documentId: string;
  filename: string;
  score: number;
  /** Textausschnitt um den ersten Treffer, für die Trefferliste. */
  snippet: string;
}

const STOPWORDS = new Set([
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem',
  'einer', 'eines', 'und', 'oder', 'aber', 'ist', 'sind', 'war', 'waren',
  'wird', 'werden', 'wurde', 'wurden', 'für', 'von', 'mit', 'auf', 'aus',
  'bei', 'nach', 'über', 'unter', 'zum', 'zur', 'nicht', 'auch', 'sich',
  'wie', 'als', 'nur', 'noch', 'sie', 'ihr', 'ihre', 'ihren', 'wir', 'uns',
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 2 && !STOPWORDS.has(token));
}

export class SessionSearchIndex {
  private entries: IndexEntry[] = [];

  /** Anzahl der indizierten Dokumente – für die Anzeige "n Dokumente durchsuchbar". */
  get size(): number {
    return this.entries.length;
  }

  async build(
    documents: OpenedDocument[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<void> {
    this.entries = [];

    for (const [index, document] of documents.entries()) {
      const text = await loadText(document);
      if (text) {
        const terms = new Map<string, number>();
        for (const token of tokenize(text)) {
          terms.set(token, (terms.get(token) ?? 0) + 1);
        }
        this.entries.push({
          documentId: document.id,
          filename: document.filename,
          text,
          terms,
        });
      }
      onProgress?.(index + 1, documents.length);
    }
  }

  /** Verwerfen beim Abmelden bzw. bei Ablauf der Freigabe (3.10 Punkt 4). */
  clear(): void {
    this.entries = [];
  }

  search(queryText: string, limit = 20): SearchHit[] {
    const queryTerms = tokenize(queryText);
    if (queryTerms.length === 0) return [];

    // Seltene Wörter sagen mehr aus als häufige – deshalb werden Treffer mit
    // der inversen Dokumenthäufigkeit gewichtet (klassisches TF-IDF).
    const documentFrequency = new Map<string, number>();
    for (const term of queryTerms) {
      documentFrequency.set(
        term,
        this.entries.filter((entry) => entry.terms.has(term)).length,
      );
    }

    const hits: SearchHit[] = [];
    for (const entry of this.entries) {
      let score = 0;
      for (const term of queryTerms) {
        const frequency = entry.terms.get(term);
        if (!frequency) continue;
        const df = documentFrequency.get(term) ?? 1;
        score += frequency * Math.log(1 + this.entries.length / df);
      }
      // Ein Treffer im Dateinamen wiegt schwer: wer "Telekom" sucht, meint
      // meistens die Datei, die so heißt.
      if (entry.filename.toLowerCase().includes(queryText.toLowerCase())) {
        score += 10;
      }
      if (score > 0) {
        hits.push({
          documentId: entry.documentId,
          filename: entry.filename,
          score,
          snippet: buildSnippet(entry.text, queryTerms),
        });
      }
    }

    return hits.sort((a, b) => b.score - a.score).slice(0, limit);
  }
}

function buildSnippet(text: string, terms: string[]): string {
  const lower = text.toLowerCase();
  let position = -1;
  for (const term of terms) {
    position = lower.indexOf(term);
    if (position !== -1) break;
  }
  if (position === -1) return text.slice(0, 160).trim();

  const start = Math.max(0, position - 60);
  const end = Math.min(text.length, position + 120);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}
